import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { AddItemForm } from "./AddItemForm";
import { EmptyItemsState } from "./EmptyItemsState";
import { FooterNav } from "./FooterNav";
import ItemsListDraggable from "./ItemsListDraggable";
import { StepCard } from "./StepCard";

import type { BusinessItem } from "../types";

type Props = {
  businessId: string | null;
  loading: boolean;
  saving: boolean;
  submitError?: string | null;
  ordered: BusinessItem[];
  canGoNext: boolean;
  showItemsError: boolean;
  itemsValidationAttempt: number;

  onAdd: (item: BusinessItem) => void;
  editingItem: BusinessItem | null;
  onUpdate: (item: BusinessItem) => void;
  onCancelEdit: () => void;

  onReorder: (next: BusinessItem[]) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;

  onBack: () => void;
  onNext: () => void;
};

export default function ItemsMobileLayout({
  businessId,
  loading,
  saving,
  submitError,
  ordered,
  canGoNext,
  showItemsError,
  itemsValidationAttempt,
  onAdd,
  editingItem,
  onUpdate,
  onCancelEdit,
  onReorder,
  onDelete,
  onEdit,
  onBack,
  onNext,
}: Props) {
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const showEvent =
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent =
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";

    const showSub = Keyboard.addListener(showEvent, () => {
      setKeyboardOpen(true);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardOpen(false);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const extraFooterGap = 16;
  const footerTopPadding = 8;

  /**
   * The footer now takes real layout space, so this padding is only breathing
   * room at the bottom of the scroll content. It is not compensating for an
   * overlaying footer.
   */
  const listBottomPadding = keyboardOpen ? 140 : 24;
  const formBottomSpacer = keyboardOpen ? 120 : 32;

  const pageHeader = useMemo(() => {
    return (
      <View style={{ marginBottom: 14 }}>
        <Text
          style={{
            fontSize: 20,
            fontWeight: "800",
            color: "#111827",
          }}
        >
          Products & Services
        </Text>

        <Text
          style={{
            marginTop: 3,
            fontSize: 13,
            lineHeight: 18,
            color: "#6B7280",
            fontWeight: "500",
          }}
        >
          Add, edit and arrange your products or services.
        </Text>

        {showItemsError ? (
          <View
            style={{
              borderRadius: 14,
              borderWidth: 1,
              borderColor: "#FCA5A5",
              backgroundColor: "#FEF2F2",
              padding: 12,
              marginTop: 12,
            }}
          >
            <Text
              style={{
                color: "#991B1B",
                fontSize: 13,
                lineHeight: 18,
                fontWeight: "800",
              }}
            >
              Please fix the highlighted fields before continuing.
            </Text>
          </View>
        ) : null}
      </View>
    );
  }, [showItemsError]);

  const addEditFooter = useMemo(() => {
    return (
      <View style={{ marginTop: ordered.length > 0 ? 8 : 14 }}>
        <Text
          style={{
            fontSize: 15,
            fontWeight: "800",
            color: "#111827",
            marginBottom: 10,
          }}
        >
          {editingItem ? "Edit item" : "Add item"}
        </Text>

        <AddItemForm
          businessId={businessId}
          onAdd={onAdd}
          editingItem={editingItem}
          onUpdate={onUpdate}
          onCancelEdit={onCancelEdit}
        />

        {(!canGoNext || showItemsError) && !keyboardOpen && (
          <Text
            style={{
              textAlign: "center",
              marginTop: 12,
              fontSize: 13,
              lineHeight: 18,
              color: showItemsError ? "#B91C1C" : "#4B5563",
              fontWeight: showItemsError ? "800" : "500",
            }}
          >
            Add at least one product or service to continue
          </Text>
        )}

        <View style={{ height: formBottomSpacer }} />
      </View>
    );
  }, [
    businessId,
    editingItem,
    onAdd,
    onCancelEdit,
    onUpdate,
    canGoNext,
    showItemsError,
    keyboardOpen,
    formBottomSpacer,
    ordered.length,
  ]);

  return (
    <SafeAreaView
      style={{ flex: 1, backgroundColor: "#FFFFFF" }}
      edges={["top", "bottom"]}
    >
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={Platform.OS === "ios" ? 74 : 0}
      >
        <View style={{ flex: 1, minHeight: 0, backgroundColor: "#FFFFFF" }}>
          <View
            style={{
              flex: 1,
              minHeight: 0,
              paddingHorizontal: 16,
              paddingTop: 8,
            }}
          >
            <StepCard
              style={{
                flex: 1,
                minHeight: 0,
                paddingBottom: 0,
              }}
            >
              {loading ? (
                <View
                  style={{
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {pageHeader}

                  <View
                    style={{
                      flex: 1,
                      alignItems: "center",
                      justifyContent: "center",
                      paddingBottom: listBottomPadding,
                    }}
                  >
                    <ActivityIndicator size="small" />

                    <Text
                      style={{
                        marginTop: 10,
                        color: "#6B7280",
                        fontSize: 13,
                        fontWeight: "600",
                      }}
                    >
                      Loading items...
                    </Text>
                  </View>
                </View>
              ) : (
                <ItemsListDraggable
                  style={{ flex: 1, minHeight: 0 }}
                  items={ordered}
                  onReorder={onReorder}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  contentBottomPad={listBottomPadding}
                  ListHeaderIntroComponent={pageHeader}
                  useScrollViewOnWeb
                  showHeader
                  showDragHint={ordered.length > 1}
                  ListEmptyComponent={
                    <View
                      style={{
                        marginBottom: 14,
                        borderRadius: 16,
                        borderWidth: showItemsError ? 1 : 0,
                        borderColor: "#EF4444",
                        padding: showItemsError ? 8 : 0,
                      }}
                    >
                      <EmptyItemsState />
                    </View>
                  }
                  ListFooterComponent={addEditFooter}
                  scrollToFooterKey={
                    editingItem?.id ??
                    (showItemsError
                      ? `items-error-${itemsValidationAttempt}`
                      : null)
                  }
                />
              )}
            </StepCard>
          </View>

          {!keyboardOpen && (
            <View
              style={{
                paddingHorizontal: 16,
                paddingTop: footerTopPadding,
                paddingBottom: extraFooterGap,
                backgroundColor: "#FFFFFF",
                borderTopWidth: 1,
                borderTopColor: "#E5E7EB",
              }}
            >
              {submitError ? (
                <Text
                  style={{
                    marginBottom: 10,
                    textAlign: "center",
                    fontSize: 13,
                    lineHeight: 18,
                    color: "#B91C1C",
                    fontWeight: "800",
                  }}
                >
                  {submitError}
                </Text>
              ) : null}

              <FooterNav
                onBack={onBack}
                onNext={onNext}
                nextDisabled={loading || saving}
                nextLabel={saving ? "Saving..." : "Next: Review ->"}
                variant="light"
                backVariant="secondary"
              />
            </View>
          )}
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
